import * as React from 'react';
import AmCharts from "@amcharts/amcharts3-react";
import { _ } from "@src/services/i18n";
import { classNames, css, mixin, vars, defaultProps, formatters } from '@src/services/theme';
import { Proto, Props, State } from '@src/components/Proto';
import { Box, Gauge, GaugeCustom } from '@src/components';

// typing

// style
const style = css`
    display: flow-root;
    > .info {
        clear: both;
        ${mixin.row}
        ${mixin.lastState}
        min-width: 100%;
    }
    .stats {
        ${mixin.row}
        padding-bottom: 20px;
        > article {
            background: #fff;
            padding: 15px;
            border: 1px solid #cccccc;
            color: #666;
            text-transform: uppercase;
            text-align: center;
            .iconBadge {
                ${mixin.iconBadge}

                > span {
                    display: inline-block;
                    width: 120px;
                    height: 120px;
                    background-color: ${vars.colors.green};
                    background-size: contain;
                    background-repeat: no-repeat;
                    background-position: center;
                    border: 5px solid #cccccc;
                    border-radius: 100%;
                    ${mixin.statusColor}

                    &.soh {
                        background-image: url("/files/icons/soh.png");
                    }
                    &.memory {
                        background-image: url("/files/icons/memory.png");
                    }
                    &.balance {
                        background-image: url("/files/icons/balance.png");
                    }
                }
            }
            .gauge-wrapper {
                display: inline-block;
                border: 5px solid #cccccc;
                border-radius: 100%;
                ${mixin.statusColor}
            }
            > h4 {
                font-size: 17px;
                text-transform: uppercase;

                > strong {
                    display: block;
                    font-size: 30px;
                    font-weight: 500;
                    text-transform: none;
                }
            }
        }
    }
`;

// component
export class LastState extends Proto<Props, State> {

	// render
	render() {
        const loading = this.loading();
        if (loading) return loading;
        const { data } = this.state;
        const { cycle } = data.data;
        const { battery } = data.data;
        const { alert } = data.data;

        let status = {
            soh: 'OK',
            balance: 'OK',
            memory: 'OK'
        };
        if (battery.avgsoh <= 45) status.soh = 'WARNING';
        if (battery.avgsoh <= 35) status.soh = 'KO';
        if (cycle.maxdeltav >= 1.7) status.balance = 'WARNING';
        if (cycle.maxdeltav >= 2.5) status.balance = 'KO';
        if (battery.wleft < 26) status.memory = 'WARNING';
        if (battery.wleft < 2) status.memory = 'KO';
        if (battery.wleft == null) status.memory = 'UNKNOWN';

        const minv = cycle.minvoltage;
        const propsMinvoltage = {
            ...minv
        };
        const maxv = cycle.maxvoltage;
        const propsMaxvoltage = {
            ...maxv
        };
        const ce = cycle.energy;
        const propsEnergy = {
            ...ce
        };

        let alertClass = 'normal';
        if(typeof alert!='undefined' && alert){
            alertClass = alert.class;
        }

        const stateClass = "state-" + alertClass;
        
		return (
            <div className={style}>
                {alert && alert.class &&
                <section className="info">
                    <article className={stateClass}>
                        <figure>
                            <span className="fa fa-warning" />
                        </figure>
                        <div>
                            <h4>{ _('laststate_title', 'BatteryOverview') }</h4>
                            <table className="table"><tbody>
                            <tr>
                                <td>{ _('laststate_type', 'BatteryOverview') }</td>
                                <td>{alert.level}</td>
                            </tr>
                            <tr>
                                <td>{ _('laststate_code', 'BatteryOverview') }</td>
                                <td>{alert.code}</td>
                            </tr>
                            <tr>
                                <td>{ _('laststate_description', 'BatteryOverview') }</td>
                                <td>{alert.description}</td>
                            </tr>
                            </tbody></table>
                        </div>
                    </article>
                    
                    <article className="actions">
                        <figure>
                            <span className="fa fa-list" />
                        </figure>
                        <div>
                            <h4>{ _('laststate_actions_title', 'BatteryOverview') }</h4>
                            <table className="table"><tbody>
                            <tr>
                                <td dangerouslySetInnerHTML={{ __html: alert.action_required}} />
                            </tr>
                            </tbody></table>
                        </div>
                    </article>
                </section>
                }
                <section className="stats">
                    <article>
                        <span className="iconBadge STATUS_WARNING">
                            <span className={classNames(`soh`, `STATUS_${status.soh}`)} />
                        </span>
                        <h4><strong>{battery.avgsoh}%</strong> { _('laststate_soh', 'BatteryOverview') }</h4>
                    </article>
                    <article>
                        <span className={classNames('gauge-wrapper', `STATUS_${status.balance}`)}>
                            <GaugeCustom bg="balancing2" needle="needle-sm" value={cycle.maxdeltav} max={4} min={-4} />
                        </span>
                        { /*<span className="iconBadge STATUS_OK">
                            <span className={classNames(`balance`, `STATUS_${status.balance}`)} />
                        </span>*/ }
                        <h4><strong>{_(cycle.balancing, 'BatteryOverview')}</strong> { _('laststate_balance', 'BatteryOverview') }</h4>
                    </article>
                    <article>
                        <span className="iconBadge STATUS_WARNING">
                            <span className={classNames(`memory`, `STATUS_${status.memory}`)} />
                        </span>
                        <h4><strong>{battery.wleft || '-'} weeks</strong> { _('laststate_memory', 'BatteryOverview') }</h4>
                    </article>
                    <article>
                        <Gauge {...propsMinvoltage} />
                        <h4><strong>{cycle.minvoltage.value_abs}V</strong> { _('laststate_minvoltage', 'BatteryOverview') }</h4>
                    </article>
                    <article>
                        <Gauge {...propsMaxvoltage} />
                        <h4><strong>{cycle.maxvoltage.value_abs}V</strong> { _('laststate_maxvoltage', 'BatteryOverview') }</h4>
                    </article>
                    <article>
                        <Gauge {...propsEnergy} />
                        <h4><strong>{cycle.energy.value_abs}Ah</strong> { _('laststate_energy', 'BatteryOverview') }</h4>
                    </article>
                </section>
            </div>
		);
	}
}



// WEBPACK FOOTER //
// ./src/components/BatteryOverview/LastState.tsx